import type { FilterDefinition, FilterValues } from "@/services/filter/filterTypes";
import { ParameterSlug_Category } from "@/services/filter/filterHelpers";

const RANGE_MIN_SUFFIX = "_min";
const RANGE_MAX_SUFFIX = "_max";

const encodeValue = (value: string): string => encodeURIComponent(value);

// Malformed percent-encoding must never crash the page — fall back to the raw (still usable) value.
const decodeValue = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const stripRangeSuffix = (key: string): string => key.replace(new RegExp(`(${RANGE_MIN_SUFFIX}|${RANGE_MAX_SUFFIX})$`), "");

/**
 * Parses filter path segments (category + `slug:v1,v2` + `slug_min:`/`slug_max:`) into the same
 * flat FilterValues shape useUrlFilter already produces from query strings — a segment addressing
 * a range bound is recognized purely by its `_min`/`_max` key suffix, exactly like the existing
 * query-string convention, so parsing never needs to know a filter's view type.
 *
 * Never throws — malformed segments (no colon, empty slug/value) are dropped so a hand-edited or
 * stale shared URL can't crash the page.
 */
export function parseFilterPath(segments: string[]): FilterValues {
  const values: FilterValues = {};
  let rest = segments;

  const [first] = rest;
  if (first !== undefined && first !== "" && !first.includes(":")) {
    values[ParameterSlug_Category] = decodeValue(first);
    rest = rest.slice(1);
  }

  for (const segment of rest) {
    const colonIndex = segment.indexOf(":");
    if (colonIndex <= 0 || colonIndex === segment.length - 1) {
      continue;
    } // no slug, or no value after ':'

    const slug = decodeValue(segment.slice(0, colonIndex));
    const parts = segment
      .slice(colonIndex + 1)
      .split(",")
      .map(decodeValue)
      .filter((part) => part !== "");

    if (slug === "" || parts.length === 0) {
      continue;
    }

    values[slug] = parts.length === 1 ? parts[0] : parts;
  }

  return values;
}

/**
 * Inverse of parseFilterPath. Orders segments deterministically: category first, then the rest
 * sorted by the matching FilterDefinition.id (ascending, min before max within the same range
 * parameter); segments with no matching definition (stale/unknown slug) sort after all known
 * ones, alphabetically, so ordering is total and deterministic even with partial/stale data.
 */
export function buildFilterPath(filterValues: FilterValues, filterDefinitions: FilterDefinition[]): string[] {
  const segments: string[] = [];

  const categoryValue = filterValues[ParameterSlug_Category];
  if (typeof categoryValue === "string" && categoryValue !== "") {
    segments.push(encodeValue(categoryValue));
  }

  const orderById = new Map(filterDefinitions.map((def) => [def.slug, def.id]));

  const entries = Object.entries(filterValues).filter((entry): entry is [string, string | string[]] => {
    const [key, value] = entry;
    return key !== ParameterSlug_Category && value !== undefined && value !== "" && !(Array.isArray(value) && value.length === 0);
  });

  entries.sort(([keyA], [keyB]) => {
    const orderA = orderById.get(stripRangeSuffix(keyA));
    const orderB = orderById.get(stripRangeSuffix(keyB));

    if (orderA !== undefined && orderB !== undefined && orderA !== orderB) {
      return orderA - orderB;
    }
    if (orderA !== undefined && orderB === undefined) {
      return -1;
    }
    if (orderA === undefined && orderB !== undefined) {
      return 1;
    }

    // Same definition (or both unknown): keep it deterministic, min before max.
    if (keyA.endsWith(RANGE_MIN_SUFFIX) && keyB.endsWith(RANGE_MAX_SUFFIX)) {
      return -1;
    }
    if (keyA.endsWith(RANGE_MAX_SUFFIX) && keyB.endsWith(RANGE_MIN_SUFFIX)) {
      return 1;
    }

    return keyA.localeCompare(keyB);
  });

  for (const [key, value] of entries) {
    const parts = Array.isArray(value) ? value : [value];
    segments.push(`${encodeValue(key)}:${parts.map(encodeValue).join(",")}`);
  }

  return segments;
}
